import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLightbulbOn } from '@fortawesome/pro-solid-svg-icons/faLightbulbOn';
import { faArrowUpRightFromSquare } from '@fortawesome/sharp-regular-svg-icons/faArrowUpRightFromSquare';

import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { Text, TextType } from 'ui-components/components/text';

import addOnMessages from '../../../pages/settings/components/add-on-tile/messages';
import { DEFAULT_TRIAL_LENGTH_IN_DAYS } from '../../../constants/add-ons';
import { UpsellModalContentCodes } from '../../../constants/upsell-modal-content-codes';
import { useEnableRateShopperFreemium } from '../../../feature-flags/hooks/use-enable-rate-shopper-freemium';
import FormattedCurrency from '../../formatted-currency';
import { getUpsellModalContent } from '../helper';
import { shippingStrategiesNewFreemiumBullets } from '../messages';

import styles from './add-on-description.module.less';

import type { Currency } from '../../../prop-types/currency-types';
import type { UpsellModalContentCode } from '../../../constants/upsell-modal-content-codes';

const redirectLinkLookup: Record<string, string> = {
  [UpsellModalContentCodes.FulfillmentProviderBundle]: '//www.shipstation.com/fulfillment-step1/',
};

type AddOnDescriptionProps = {
  upsellModalContentCode: UpsellModalContentCode;
  canTrial: boolean;
  trialLengthInDays?: number;
  addOnPrice: Currency;
};

export const AddOnDescription = ({
  upsellModalContentCode,
  canTrial,
  trialLengthInDays = DEFAULT_TRIAL_LENGTH_IN_DAYS,
  addOnPrice,
}: AddOnDescriptionProps) => {
  const intl = useIntl();
  const content = getUpsellModalContent(upsellModalContentCode);
  const isRateShopperFreemiumEnabled = useEnableRateShopperFreemium();

  const { longFormDescription, redirectTitle: addOnRedirectTitle, redirectText: addOnRedirectText } = content;

  const getBulletContent = () => {
    if (upsellModalContentCode === UpsellModalContentCodes.ShippingStrategies && isRateShopperFreemiumEnabled) {
      return shippingStrategiesNewFreemiumBullets;
    }
    return content.bullets;
  };

  const bulletContent = getBulletContent();

  const taglineValue = canTrial ? content.trialTagline : content.tagline;

  return (
    <>
      {taglineValue ? <Text className={styles.tagline} textType={TextType.H2} value={taglineValue} /> : null}
      {canTrial ? (
        <Text
          className={styles['trial-tagline']}
          formattedMessageValues={{
            trialLengthInDays,
            formattedDays: intl.formatNumber(trialLengthInDays),
          }}
          textType={TextType.H2}
          value={addOnMessages.freeForXDays}
        />
      ) : null}
      {longFormDescription ? (
        <Text
          className={styles['long-form-description']}
          formattedMessageValues={{ br: <br /> }}
          textType={TextType.Paragraph}
          value={longFormDescription}
        />
      ) : null}
      {addOnPrice.value !== 0 && content.name ? (
        <div className={styles['name-and-price']}>
          <Text textType={TextType.H3} value={content.name} />
          <div className={styles.price}>
            <div className={styles.amount}>
              <FormattedCurrency currencyCode={addOnPrice.code} fractionDigits={0} value={addOnPrice.value} />
            </div>
            <Text className={styles['per-month']} textType={TextType.Caption} value={addOnMessages.perMonthFullText} />
          </div>
        </div>
      ) : null}
      {bulletContent ? (
        <ul className={styles.bullets}>
          <FormattedMessage {...bulletContent} values={{ li: (chunks) => <li>{chunks}</li> }} />
        </ul>
      ) : null}
      {addOnRedirectText ? (
        <div className={styles['redirect-message-box']}>
          {addOnRedirectTitle ? (
            <Text
              className={styles['redirect-title-text']}
              formattedMessageValues={{
                icon: <FontAwesomeIcon className={styles['redirect-title-icon']} icon={faLightbulbOn} />,
              }}
              textType={TextType.Label}
              value={addOnRedirectTitle}
            />
          ) : null}
          <Text
            className={styles['redirect-text']}
            formattedMessageValues={{
              a: (chunks) => (
                <Button href={redirectLinkLookup[upsellModalContentCode]} theme='link'>
                  {chunks}
                  <FontAwesomeIcon className={styles['redirect-text-icon']} icon={faArrowUpRightFromSquare} />
                </Button>
              ),
            }}
            textType={TextType.Paragraph}
            value={addOnRedirectText}
          />
        </div>
      ) : null}
    </>
  );
};
